import type { RolePermissions } from "./permissions.js";

export interface AdminAccessEntry {
  email: string;
  role: "admin" | "super" | "scoped";
  scope?: {
    school_ids?: string[];
    grade_levels?: string[];
    english_language_learners?: boolean;
    student_ids?: string[];
  };
  school_id?: string;
  /**
   * Optional per-person data visibility overrides.
   * Merges on top of the role-based defaults from config/rolePermissions.
   * Use when a specific person needs different access than their staff_type default
   * (e.g. a Counselor role who also needs behavior data).
   */
  dataPermissions?: Partial<RolePermissions>;
  /**
   * Named capabilities this person holds, e.g. `privileges.read`.
   *
   * Deliberately per-person rather than per-role. "Our main office" is two or
   * three named people, not an Aspen `staff_type`, and `rolePermissions` is
   * structurally per-role — `docs/access.md` already flags that limitation. A
   * capability string also scales to the next program without a schema
   * change.
   *
   * **Never write these from the CSV importer.** `lookups/admins` is a merge
   * accumulator that never clears an omitted field, so a capability written by
   * a nightly import would be permanent and invisible. `importAdminsCsv` skips
   * any email holding a grant document, which is what keeps this safe — do not
   * remove that skip.
   */
  capabilities?: string[];
  /**
   * Which parts of the app this person is offered at all.
   *
   * **Absent means today's derived behaviour** — the staff dashboard if you have
   * a class roster, the admin dashboard if you hold a grant — so no grant
   * written before this field existed changes and nothing needs migrating. The
   * field only ever *narrows*, and only for an account somebody deliberately
   * edited.
   *
   * An explicit empty list means **nothing**, not "the default": somebody
   * clearing every box is saying so. That makes it possible to save a grant
   * offering its holder no way in, so the grant editor refuses to save one
   * rather than the model quietly reinterpreting it. See
   * `docs/program-access.md` §6a.
   */
  surfaces?: Surface[];
  /**
   * Which offices this person works in — "Athletics office", "Main office".
   *
   * Free-form names rather than a closed list, because a school's offices are
   * its own business and a fifth should cost nothing. A program's access block
   * can then say "the office" (every office account, as before) or name the
   * offices it is for; see `officeAudience()` in `programAccess.ts`. The names
   * only mean anything alongside a privileges capability, which is what makes
   * somebody office staff at all.
   */
  offices?: string[];
}

/**
 * A part of the app a person is offered at all.
 *
 * Named surfaces rather than route prefixes, because a route is an
 * implementation detail that moves — `/staff/privileges` was `/admin/privileges`
 * once already — while a surface is a thing a person is given. Which routes
 * belong to one is the app's business, not the grant's.
 *
 * This is the third axis of `docs/program-access.md` §1, and it is neither a
 * permission nor a role. A surface decides what is *offered*, never what is
 * *allowed*: taking one away hides tiles, not documents, and `scope`, the
 * program's access block and `firestore.rules` are still what decide access. It
 * also does not replace `role`, which is about authority — a `scoped` grant can
 * hold `admin`, and a `super` can be given only `programs`.
 */
export type Surface = "staff" | "admin" | "programs";

/** The closed list, so a typo in a grant is findable. */
export const SURFACES = {
  /** The class-roster dashboard: sections, caseload, interventions. */
  STAFF: "staff",
  /** The admin dashboard: students, analysis, grants, settings. */
  ADMIN: "admin",
  /** Program tiles and the desk lookup. */
  PROGRAMS: "programs",
} as const;

/** How each surface should read to whoever is granting it. */
export const SURFACE_LABELS = {
  [SURFACES.STAFF]: {
    label: "Staff",
    description: "Sections, caseload, rosters and interventions.",
  },
  [SURFACES.ADMIN]: {
    label: "Admin",
    description: "Students, analysis, grants and settings.",
  },
  [SURFACES.PROGRAMS]: {
    label: "Programs",
    description: "Program tiles and the privileges desk lookup.",
  },
} satisfies Record<Surface, { label: string; description: string }>;

/**
 * Whether a grant offers a surface.
 *
 * Three states, and the two that look alike are the whole point:
 *
 * - **No list.** The grant has never said, so the answer is whatever the app
 *   derived before this field existed — which this cannot know, so the caller
 *   passes it in as `derived`.
 * - **An empty list.** Somebody cleared every box, which means nothing, not the
 *   default. The editor will not save one, but a grant hand-written into
 *   Firestore can still carry it and has to mean what it says.
 * - **A list.** Exactly what it names.
 */
export function grantOffersSurface(
  entry: Pick<AdminAccessEntry, "surfaces"> | undefined | null,
  surface: Surface,
  derived: boolean,
): boolean {
  const surfaces = entry?.surfaces;
  if (!surfaces) return derived;
  return surfaces.includes(surface);
}

export interface AdminAccessGrant {
  author_email: string;
  target_email: string;
  grant: AdminAccessEntry;
  granted_at: string; // ISO 8601
  expiration?: string; // ISO 8601
  note?: string;
}

export interface AdminLookup {
  [email: string]: AdminAccessEntry;
}

export interface StaffEmailIndex {
  [email: string]: string;
}

export interface AccessPaths {
  "admin_access_grants/{target_email}": AdminAccessGrant;
}

/**
 * Capabilities the app defines. A closed list so a typo in a grant is findable.
 *
 * Program capabilities are read/write on the *records* — the flags, holds and
 * overrides humans enter. Nobody gets a write capability on a computed status;
 * that is `allow write: if false` for every client, always.
 */
export const CAPABILITIES = {
  /** See a program's criteria and any student's status. */
  PROGRAMS_READ: "privileges.read",
  /** Tick flags, place holds, record overrides. */
  PROGRAMS_WRITE: "privileges.write",
  /** Tick only the flags a policy marks as the advisor's own. */
  PROGRAMS_ADVISOR: "privileges.advisor",
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

/**
 * How each capability should read to whoever is granting it.
 *
 * **Named for the role it confers, not for the power it sounds like.** The
 * first version of this labelled `privileges.write` "Manage programs", which
 * read as full control of the program and is not what it does: it makes
 * somebody the *office*, and how much the office may do — which boxes, whether
 * they may place a hold — is a per-program decision made in the policy editor.
 * An administrator reading the two screens in one sitting got two vocabularies
 * for one idea and no way to tell which capability meant "the front desk".
 *
 * So these say **what kind of person** this is on a program, and the policy
 * says **how much** that kind may do. The ids stay the wire format
 * `firestore.rules` tests.
 *
 * Typed against `Capability`, so adding one to the closed list above fails the
 * build here until somebody has said what it means — which is the only thing
 * stopping a new capability from quietly never appearing in the picker.
 */
export const CAPABILITY_LABELS = {
  [CAPABILITIES.PROGRAMS_READ]: {
    label: "Viewer",
    description:
      "Can view a program's criteria and any student's status, but cannot make changes.",
  },
  [CAPABILITIES.PROGRAMS_WRITE]: {
    label: "Office staff",
    description:
      "Can enter data or view data, depending on program settings for office staff.",
  },
  /**
   * Legacy. Advisor access is now derived automatically from each staff
   * member's own Advisory section in Aspen (`shared/src/schemas/sections.ts`'s
   * `isAdvisorySection()`), not granted here -- see `docs/program-access.md`.
   * Kept in the closed list so old grants that still carry it keep validating;
   * nothing reads it to decide advisor-ness any more.
   */
  [CAPABILITIES.PROGRAMS_ADVISOR]: {
    label: "Advisor",
    description:
      "No longer needed: advisor access now follows a staff member's own Advisory section automatically.",
  },
} satisfies Record<Capability, { label: string; description: string }>;

/**
 * Whether a grant entry carries a capability.
 *
 * Mirrors the `hasCapability()` in `firestore.rules`, and exists so the UI can
 * gate a control on the same condition the rules will enforce. The rules remain
 * the boundary; this only decides whether to draw a button that would fail.
 */
export function hasCapability(
  entry: Pick<AdminAccessEntry, "capabilities" | "role"> | undefined | null,
  capability: string,
): boolean {
  if (!entry) return false;
  // A super admin holds everything, as they do everywhere else in the app.
  if (entry.role === "super") return true;
  return (entry.capabilities ?? []).includes(capability);
}
